// File: /src/hooks/useDashboardData.js

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import apiClient from '../services/apiClient';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { fetchLogs } from '../services/LogsService';
import fetchMarketSnapshot from '../services/marketDataService';

const INDICATOR_STORAGE_KEY = 'indicatorData';

const createDefaultIndicatorSnapshot = () => ({
	previous: {
		dollar: null,
		euro: null,
		pound: null,
		bitcoin: null,
		ethereum: null,
	},
	trend: {
		dollar: 0,
		euro: 0,
		pound: 0,
		bitcoin: 0,
		ethereum: 0,
	},
});

const mapSnapshotToIndicators = (snapshot) => ({
	dollar: {
		value: snapshot?.previous?.dollar ?? 0,
		trend: snapshot?.trend?.dollar ?? 0,
	},
	euro: {
		value: snapshot?.previous?.euro ?? 0,
		trend: snapshot?.trend?.euro ?? 0,
	},
	pound: {
		value: snapshot?.previous?.pound ?? 0,
		trend: snapshot?.trend?.pound ?? 0,
	},
	bitcoin: {
		value: snapshot?.previous?.bitcoin ?? 0,
		trend: snapshot?.trend?.bitcoin ?? 0,
	},
	ethereum: {
		value: snapshot?.previous?.ethereum ?? 0,
		trend: snapshot?.trend?.ethereum ?? 0,
	},
});

const parseStoredSnapshot = () => {
	try {
		const stored = localStorage.getItem(INDICATOR_STORAGE_KEY);
		if (!stored) {
			return createDefaultIndicatorSnapshot();
		}
		const parsed = JSON.parse(stored);
		return {
			previous: {
				...createDefaultIndicatorSnapshot().previous,
				...(parsed?.previous ?? {}),
			},
			trend: {
				...createDefaultIndicatorSnapshot().trend,
				...(parsed?.trend ?? {}),
			},
		};
	} catch (error) {
		console.warn(
			'Não foi possível carregar o cache de indicadores.',
			error,
		);
		return createDefaultIndicatorSnapshot();
	}
};

const calcTrend = (newVal, prevVal, oldTrend) => {
	if (prevVal === null || prevVal === undefined) {
		return 0;
	}
	const diff = newVal - prevVal;
	return diff === 0 ? oldTrend : diff;
};

const normalizeFinanceSummary = (summary = {}) => {
	const totalIncome = Number(summary.totalIncome) || 0;
	const totalExpense = Number(summary.totalExpense) || 0;
	const totalReceived = Number(summary.totalReceived) || 0;
	const totalPending = Number(summary.totalPending) || 0;
	const totalOverdue = Number(summary.totalOverdue) || 0;
	const totalRevenue = totalIncome + totalReceived;
	const totalDeductions = totalExpense + totalOverdue;

	return {
		totalIncome,
		totalExpense,
		totalReceived,
		totalPending,
		totalOverdue,
		netTotal:
			typeof summary.netTotal === 'number'
				? summary.netTotal
				: totalRevenue - totalDeductions,
	};
};

const mapToCalendarEvents = (projects = [], invoices = [], calendar = []) => {
	const projectEvents = projects.map((project) => ({
		id: `project-${project.id}`,
		title: project.name,
		start: new Date(project.start_date),
		end: new Date(project.end_date),
		type: 'project',
	}));

	const invoiceEvents = invoices.map((invoice) => ({
		id: `invoice-${invoice.id}`,
		title: `Fatura: ${invoice.description}`,
		start: new Date(invoice.due_date),
		end: new Date(invoice.due_date),
		type: 'invoice',
	}));

	const calendarEvents = calendar.map((event) => ({
		id: `calendar-${event.id}`,
		title: event.title,
		start: new Date(event.start_date),
		end: new Date(event.end_date),
		type: 'calendar',
	}));

	return [...projectEvents, ...invoiceEvents, ...calendarEvents];
};

const toDate = (value) => {
	if (!value) return null;
	const date = new Date(value);
	return Number.isNaN(date) ? null : date;
};

const formatEntityList = (items, mapper) => {
	const names = items
		.map(mapper)
		.filter(Boolean);

	if (names.length === 0) return '';
	if (names.length === 1) return names[0];
	if (names.length === 2) return `${names[0]} e ${names[1]}`;

	const remaining = names.length - 2;
	return `${names[0]}, ${names[1]}${remaining > 0 ? ` e mais ${remaining}` : ''}`;
};

const normalizeText = (value) => {
	if (typeof value !== 'string') {
		return '';
	}

	try {
		return value
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.trim()
			.toLowerCase();
	} catch (error) {
		return value.trim().toLowerCase();
	}
};

const matchesKeyword = (value, keywords) =>
	keywords.some(
		(keyword) => value === keyword || value.includes(keyword),
	);

const getProjectStatusKey = (status) => {
	const normalized = normalizeText(status);
	if (!normalized) return 'indefinido';

	if (
		matchesKeyword(normalized, [
			'completed',
			'concluido',
			'concluida',
			'concluidas',
			'concluidos',
			'finalizado',
			'finalizada',
			'finalizados',
			'finalizadas',
		])
	) {
		return 'completed';
	}

	if (
		matchesKeyword(normalized, [
			'in_progress',
			'in progress',
			'emandamento',
			'em andamento',
			'andamento',
		])
	) {
		return 'in_progress';
	}

	if (
		matchesKeyword(normalized, [
			'pending',
			'pendente',
			'pendentes',
			'planejamento',
			'em planejamento',
		])
	) {
		return 'pending';
	}

	if (
		matchesKeyword(normalized, [
			'cancelled',
			'cancelado',
			'cancelada',
			'cancelados',
		])
	) {
		return 'cancelled';
	}

	if (
		matchesKeyword(normalized, ['paused', 'pausado', 'pausada', 'pausados'])
	) {
		return 'paused';
	}

	return normalized;
};

const getInvoiceStatusKey = (status) => {
	const normalized = normalizeText(status);
	if (!normalized) return 'unknown';

	if (matchesKeyword(normalized, ['paid', 'pago', 'quitado', 'quitada'])) {
		return 'paid';
	}

	if (
		matchesKeyword(normalized, [
			'overdue',
			'atraso',
			'atrasada',
			'atrasadas',
			'atrasado',
		])
	) {
		return 'overdue';
	}

	if (
		matchesKeyword(normalized, [
			'pending',
			'pendente',
			'pendentes',
			'em aberto',
			'aberto',
			'aberta',
			'aguardando',
			'aguarda',
			'em cobranca',
		])
	) {
		return 'pending';
	}

	if (
		matchesKeyword(normalized, [
			'cancelled',
			'cancelado',
			'cancelada',
			'canceladas',
		])
	) {
		return 'cancelled';
	}

	return normalized;
};

export const useDashboardData = () => {
	const [user, setUser] = useState(null);
	const [loadingUser, setLoadingUser] = useState(true);

	const [clientsCount, setClientsCount] = useState(0);
	const [projectsCount, setProjectsCount] = useState(0);
	const [invoicesCount, setInvoicesCount] = useState(0);
	const [projectStatusCounts, setProjectStatusCounts] = useState({});
	const [loadingSummary, setLoadingSummary] = useState(true);
	const [summaryError, setSummaryError] = useState(null);

	const [financeSummary, setFinanceSummary] = useState(() =>
		normalizeFinanceSummary(),
	);

	const [calendarEvents, setCalendarEvents] = useState([]);
	const [loadingEvents, setLoadingEvents] = useState(true);

	const [activities, setActivities] = useState([]);
	const [loadingActivities, setLoadingActivities] = useState(true);
	const [alerts, setAlerts] = useState([]);

	const initialSnapshot = useMemo(parseStoredSnapshot, []);
	const [indicators, setIndicators] = useState(() =>
		mapSnapshotToIndicators(initialSnapshot),
	);
	const [indicatorsLoading, setIndicatorsLoading] = useState(true);

	const previousValuesRef = useRef(initialSnapshot.previous);
	const trendRef = useRef(initialSnapshot.trend);

	const persistSnapshot = useCallback((snapshot) => {
		localStorage.setItem(INDICATOR_STORAGE_KEY, JSON.stringify(snapshot));
	}, []);

	useEffect(() => {
		const fetchUser = async () => {
			try {
				const response = await apiClient.get(API_ENDPOINTS.USERS_ME);
				setUser(response.data);
			} catch (error) {
				console.error('Erro ao buscar usuário:', error);
			} finally {
				setLoadingUser(false);
			}
		};

		fetchUser();
	}, []);

	useEffect(() => {
		const fetchSummaryData = async () => {
			try {
				setLoadingSummary(true);
				setSummaryError(null);

				const [clientsRes, projectsRes, invoicesRes] =
					await Promise.all([
						apiClient.get(API_ENDPOINTS.CLIENTS),
						apiClient.get(API_ENDPOINTS.PROJECTS),
						apiClient.get(API_ENDPOINTS.INVOICES),
					]);

				const clientsData = clientsRes.data || [];
				const projectsData = projectsRes.data || [];
				const invoicesData = invoicesRes.data || [];

				setClientsCount(clientsData.length);
				setProjectsCount(projectsData.length);
				setInvoicesCount(invoicesData.length);

				const statusCounts = projectsData.reduce((acc, project) => {
					if (!project) return acc;
					const statusKey = getProjectStatusKey(project.status);
					acc[statusKey] = (acc[statusKey] || 0) + 1;
					return acc;
				}, {});

				setProjectStatusCounts(statusCounts);

				const today = new Date();
				const startOfToday = new Date(
					today.getFullYear(),
					today.getMonth(),
					today.getDate(),
				);
				const invoicedProjectIds = new Set();
				const overdueInvoices = [];
				const pendingInvoices = [];

				invoicesData.forEach((invoice) => {
					if (!invoice) return;

					if (invoice.projectId != null) {
						invoicedProjectIds.add(String(invoice.projectId));
					}

					const statusKey = getInvoiceStatusKey(invoice.status);

					if (statusKey === 'overdue') {
						overdueInvoices.push(invoice);
					}

					if (statusKey === 'pending') {
						pendingInvoices.push(invoice);
					}
				});

				const delayedProjects = projectsData.filter((project) => {
					if (!project) return false;
					const statusKey = getProjectStatusKey(project.status);
					if (statusKey === 'completed' || statusKey === 'cancelled') {
						return false;
					}
					const endDate = toDate(project.end_date);
					return endDate && endDate < startOfToday;
				});

				const completedWithoutInvoice = projectsData.filter((project) => {
					if (!project) return false;
					const statusKey = getProjectStatusKey(project.status);
					if (statusKey !== 'completed') {
						return false;
					}
					const projectIdKey =
						project.id != null ? String(project.id) : null;
					if (!projectIdKey) {
						return false;
					}
					return !invoicedProjectIds.has(projectIdKey);
				});

				const activeProjects = projectsData.filter((project) => {
					if (!project) return false;
					const statusKey = getProjectStatusKey(project.status);
					return statusKey === 'pending' || statusKey === 'in_progress';
				});

				const activeClientIds = new Set(
					projectsData
						.filter((project) => {
							if (!project || project.clientId == null) return false;
							const statusKey = getProjectStatusKey(project.status);
							return (
								statusKey !== 'completed' &&
								statusKey !== 'cancelled'
							);
						})
						.map((project) => project.clientId),
				);

				const idleClients = clientsData.filter(
					(client) => client && !activeClientIds.has(client.id)
				);

				const newAlerts = [];

				const addInsight = ({
					id,
					type,
					count,
					label,
					getPositiveMessage,
					zeroMessage,
				}) => {
					const message =
						count > 0
							? (typeof getPositiveMessage === 'function'
									? getPositiveMessage()
									: getPositiveMessage) || ''
							: zeroMessage;

					newAlerts.push({
						id,
						type,
						count,
						label,
						message,
					});
				};

				addInsight({
					id: 'overdue-invoices',
					type: 'danger',
					count: overdueInvoices.length,
					label: 'Faturas atrasadas',
					getPositiveMessage: () => {
						const grouped = overdueInvoices.reduce((acc, invoice) => {
							const projectIdKey =
								invoice?.projectId != null
									? String(invoice.projectId)
									: '__no_project__';
							if (!acc[projectIdKey]) {
								acc[projectIdKey] = {
									project:
										projectIdKey === '__no_project__'
											? null
											: projectsData.find(
													(project) =>
														project &&
														String(project.id) === projectIdKey,
											  ) || null,
									invoices: [],
								};
							}
							acc[projectIdKey].invoices.push(invoice);
							return acc;
						}, {});

						const message = Object.values(grouped)
							.map((entry) => {
								const projectName =
									entry.project?.name ||
									entry.invoices[0]?.project?.name ||
									entry.invoices[0]?.client?.name ||
									null;
								const invoicesList = formatEntityList(
									entry.invoices,
									(invoice) =>
										invoice.description ||
										`Fatura #${invoice.id}`,
								);
								if (projectName) {
									return `${projectName}: ${invoicesList}`;
								}
								return invoicesList;
							})
							.filter(Boolean)
							.join('; ');

						return message || 'Revise as cobranças em atraso.';
					},
					zeroMessage: 'Nenhuma fatura em atraso.',
				});

				addInsight({
					id: 'pending-invoices',
					type: 'warning',
					count: pendingInvoices.length,
					label: 'Faturas em aberto',
					getPositiveMessage: () => {
						const pendingList = formatEntityList(
							pendingInvoices,
							(invoice) =>
								invoice.description || `Fatura #${invoice.id}`,
						);
						return pendingList
							? `Inclui ${pendingList}.`
							: 'Revise as cobranças pendentes.';
					},
					zeroMessage: 'Nenhuma fatura em aberto.',
				});

				addInsight({
					id: 'delayed-projects',
					type: 'danger',
					count: delayedProjects.length,
					label: 'Projetos atrasados',
					getPositiveMessage: () => {
						const projectsList = formatEntityList(
							delayedProjects,
							(project) => project.name,
						);
						return projectsList
							? `Ajuste o planejamento de ${projectsList}.`
							: 'Revise o cronograma dos projetos em atraso.';
					},
					zeroMessage: 'Nenhum projeto em atraso.',
				});

				addInsight({
					id: 'completed-without-invoice',
					type: 'warning',
					count: completedWithoutInvoice.length,
					label: 'Projetos concluídos sem faturar',
					getPositiveMessage: () => {
						const projectsList = formatEntityList(
							completedWithoutInvoice,
							(project) => project.name,
						);
						return projectsList
							? `Garanta o faturamento de ${projectsList}.`
							: 'Garanta o faturamento dos projetos concluídos.';
					},
					zeroMessage: 'Todos os projetos concluídos foram faturados.',
				});

				addInsight({
					id: 'idle-clients',
					type: 'info',
					count: idleClients.length,
					label: 'Clientes sem projetos ativos',
					getPositiveMessage: () =>
						`Reative oportunidades com ${formatEntityList(
							idleClients,
							(client) => client.name,
						)}.`,
					zeroMessage: 'Todos os clientes possuem projetos ativos.',
				});

				addInsight({
					id: 'active-projects',
					type: 'info',
					count: activeProjects.length,
					label: 'Projetos ativos',
					getPositiveMessage: () =>
						`Principais: ${formatEntityList(
							activeProjects.slice(0, 3),
							(project) => project.name,
						)}.`,
					zeroMessage: 'Nenhum projeto em andamento no momento.',
				});

				setAlerts(newAlerts);
			} catch (error) {
				console.error('Erro ao buscar resumo do dashboard:', error);
				setSummaryError(
					'Erro ao carregar dados.',
				);
			} finally {
				setLoadingSummary(false);
			}
		};

		fetchSummaryData();
	}, []);

	useEffect(() => {
		const fetchFinanceSummary = async () => {
			try {
				const response = await apiClient.get(
					API_ENDPOINTS.FINANCE_SUMMARY,
				);
				setFinanceSummary(normalizeFinanceSummary(response.data));
			} catch (error) {
				console.error('Erro ao buscar dados financeiros:', error);
			}
		};

		fetchFinanceSummary();
	}, []);

	useEffect(() => {
		const fetchEvents = async () => {
			try {
				const [projectsRes, invoicesRes, calendarRes] =
					await Promise.all([
						apiClient.get(API_ENDPOINTS.PROJECTS),
						apiClient.get(API_ENDPOINTS.INVOICES),
						apiClient.get(API_ENDPOINTS.CALENDAR),
					]);
				setCalendarEvents(
					mapToCalendarEvents(
						projectsRes.data,
						invoicesRes.data,
						calendarRes.data,
					),
				);
			} catch (error) {
				console.error('Erro ao buscar eventos do calendário:', error);
			} finally {
				setLoadingEvents(false);
			}
		};

		fetchEvents();
	}, []);

	useEffect(() => {
		const loadActivities = async () => {
			try {
				const logs = await fetchLogs();
				setActivities(logs);
			} catch (error) {
				console.error('Erro ao buscar atividades:', error);
			} finally {
				setLoadingActivities(false);
			}
		};

		loadActivities();
	}, []);

	const fetchIndicators = useCallback(async () => {
		setIndicatorsLoading(true);
		try {
			const newValues = await fetchMarketSnapshot();

			const normalizedValues = {
				dollar: Number(newValues.dollar) || 0,
				euro: Number(newValues.euro) || 0,
				pound: Number(newValues.pound) || 0,
				bitcoin: Number(newValues.bitcoin) || 0,
				ethereum: Number(newValues.ethereum) || 0,
			};

			const currentSnapshot = {
				previous: previousValuesRef.current,
				trend: trendRef.current,
			};

			const nextTrend = {
				dollar: calcTrend(
					normalizedValues.dollar,
					currentSnapshot.previous.dollar,
					currentSnapshot.trend.dollar,
				),
				euro: calcTrend(
					normalizedValues.euro,
					currentSnapshot.previous.euro,
					currentSnapshot.trend.euro,
				),
				pound: calcTrend(
					normalizedValues.pound,
					currentSnapshot.previous.pound,
					currentSnapshot.trend.pound,
				),
				bitcoin: calcTrend(
					normalizedValues.bitcoin,
					currentSnapshot.previous.bitcoin,
					currentSnapshot.trend.bitcoin,
				),
				ethereum: calcTrend(
					normalizedValues.ethereum,
					currentSnapshot.previous.ethereum,
					currentSnapshot.trend.ethereum,
				),
			};

			previousValuesRef.current = normalizedValues;
			trendRef.current = nextTrend;

			const snapshotToPersist = {
				previous: normalizedValues,
				trend: nextTrend,
			};

			persistSnapshot(snapshotToPersist);
			setIndicators(mapSnapshotToIndicators(snapshotToPersist));
		} catch (error) {
			console.error('Erro ao buscar cotações:', error);
			setIndicators(mapSnapshotToIndicators(previousValuesRef.current));
		} finally {
			setIndicatorsLoading(false);
		}
	}, [persistSnapshot]);

	useEffect(() => {
		let active = true;

		const loadIndicators = async () => {
			await fetchIndicators();
			if (!active) return;
		};

		loadIndicators();
		const interval = setInterval(fetchIndicators, 130000);

		return () => {
			active = false;
			clearInterval(interval);
		};
	}, [fetchIndicators]);

	return {
		user,
		loadingUser,
		clientsCount,
		projectsCount,
		invoicesCount,
		loadingSummary,
		summaryError,
		financeSummary,
		calendarEvents,
		loadingEvents,
		activities,
		loadingActivities,
		indicators,
		indicatorsLoading,
		projectStatusCounts,
		alerts,
		refreshIndicators: fetchIndicators,
	};
};

export default useDashboardData;
